import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { FinancialForm } from '@/components/forms/profile/FinancialForm';
import { OtherFinancialForm } from '@/components/forms/profile/OtherFinancialForm';
import { FinancialFormSchema } from '@/components/forms/schemas/FinancialFormSchema';
import { OtherFinancialFormSchema } from '@/components/forms/schemas/OtherFinancialFormSchema';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/ui/use-toast';
import { useCreateZeeFinancial } from '@/hooks/zee-financial/useCreateZeeFinancial';
import { useGetZeeFinancial } from '@/hooks/zee-financial/useGetZeeFinancial';
import { useUpdateZeeFinancial } from '@/hooks/zee-financial/useUpdateZeeFinancial';
import { useZeeStore } from '@/stores/useZeeStore';

type FinancialFormValues = z.infer<typeof FinancialFormSchema>;
type OtherFinancialFormValues = z.infer<typeof OtherFinancialFormSchema>;

const FinancialTab = ({
  setIsDirty,
}: {
  setIsDirty: (isDirty: boolean) => void;
}) => {
  const financialForm = useForm<FinancialFormValues>({
    resolver: zodResolver(FinancialFormSchema),
    defaultValues: {},
  });
  const otherFinancialForm = useForm<OtherFinancialFormValues>({
    resolver: zodResolver(OtherFinancialFormSchema),
    defaultValues: {},
  });

  const {
    zeeFinancial,
    zeeOtherFinancial,
    setZeeFinancial,
    setZeeOtherFinancial,
  } = useZeeStore();

  const {
    result: zeeFinancialResult,
    isError: isZeeFinancialError,
    isSuccess: isZeeFinancialSuccess,
    isLoading: isZeeFinancialLoading,
  } = useGetZeeFinancial();
  const {
    mutate: createZeeFinancial,
    result: createZeeFinancialResult,
    isError: isCreateZeeFinancialError,
    isSuccess: isCreateZeeFinancialSuccess,
    isLoading: isCreateZeeFinancialLoading,
  } = useCreateZeeFinancial();
  const {
    mutate: updateZeeFinancial,
    result: updateZeeFinancialResult,
    isError: isUpdateZeeFinancialError,
    isSuccess: isUpdateZeeFinancialSuccess,
    isLoading: isUpdateZeeFinancialLoading,
  } = useUpdateZeeFinancial();

  const resetForms = useCallback(() => {
    financialForm.reset({
      ...zeeFinancial,
    });

    otherFinancialForm.reset({
      ...zeeOtherFinancial,
      other_debt_description: zeeOtherFinancial?.other_debt_description || null,
      other_assets_description:
        zeeOtherFinancial?.other_assets_description || null,
    });
    setIsDirty(false);
  }, [zeeFinancial, zeeOtherFinancial, setIsDirty]);

  // Update isDirty state whenever either form state changes
  useEffect(() => {
    setIsDirty(
      financialForm.formState.isDirty || otherFinancialForm.formState.isDirty
    );
  }, [
    financialForm.formState.isDirty,
    otherFinancialForm.formState.isDirty,
    setIsDirty,
  ]);

  const handleSubmit = useCallback(async (): Promise<void> => {
    const isFinancialFormValid = await financialForm.trigger();
    const isOtherFinancialFormValid = await otherFinancialForm.trigger();
    if (isFinancialFormValid && isOtherFinancialFormValid) {
      const submitPayload = {
        ...financialForm.getValues(),
        other_financial: otherFinancialForm.getValues(),
      };

      if (!zeeFinancial && !zeeOtherFinancial) {
        createZeeFinancial(submitPayload);
      } else {
        updateZeeFinancial(submitPayload);
      }
    }
  }, [
    financialForm,
    otherFinancialForm,
    zeeFinancial,
    zeeOtherFinancial,
    createZeeFinancial,
    updateZeeFinancial,
  ]);

  const renderEditButtons = () => {
    return (
      <div className="flex w-full justify-end">
        <div className="flex gap-1">
          <Button size="sm" className="w-full md:w-fit" onClick={handleSubmit}>
            Save Changes
          </Button>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (isZeeFinancialError) {
      toast({
        title: 'Something went wrong',
        description: 'Please try again',
        variant: 'destructive',
      });
    }
  }, [isZeeFinancialError]);

  useEffect(() => {
    if (isZeeFinancialSuccess && zeeFinancialResult?.data) {
      if (zeeFinancialResult?.data?.zee_financial) {
        setZeeFinancial(zeeFinancialResult?.data?.zee_financial);
      }

      if (zeeFinancialResult?.data?.zee_other_financial) {
        setZeeOtherFinancial(zeeFinancialResult?.data?.zee_other_financial);
      }

      resetForms();
    }
  }, [
    zeeFinancialResult,
    isZeeFinancialSuccess,
    resetForms,
    setZeeFinancial,
    setZeeOtherFinancial,
  ]);

  useEffect(() => {
    if (isCreateZeeFinancialError || isUpdateZeeFinancialError) {
      toast({
        title: 'Something went wrong',
        description: 'Please try again',
        variant: 'destructive',
      });
    }
  }, [isCreateZeeFinancialError, isUpdateZeeFinancialError]);

  useEffect(() => {
    if (isCreateZeeFinancialSuccess || isUpdateZeeFinancialSuccess) {
      if (createZeeFinancialResult?.data) {
        setZeeFinancial(createZeeFinancialResult?.data?.zee_financial);
        setZeeOtherFinancial(
          createZeeFinancialResult?.data?.zee_other_financial
        );
        resetForms();
      }
      if (updateZeeFinancialResult?.data) {
        setZeeFinancial(updateZeeFinancialResult?.data?.zee_financial);
        setZeeOtherFinancial(
          updateZeeFinancialResult?.data?.zee_other_financial
        );
        resetForms();
      }
      toast({ description: 'Changes saved successfully' });
    }
  }, [
    createZeeFinancialResult,
    updateZeeFinancialResult,
    isCreateZeeFinancialSuccess,
    isUpdateZeeFinancialSuccess,
    resetForms,
    setZeeFinancial,
    setZeeOtherFinancial,
  ]);

  // no parent save registration; users save manually

  if (isZeeFinancialLoading) {
    return (
      <div className="flex h-full w-full flex-1 items-center justify-center">
        <Loader2 className="size-4 animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative space-y-1.5">
      {(isCreateZeeFinancialLoading || isUpdateZeeFinancialLoading) && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-gray-100/50 backdrop-blur-sm">
          <Loader2 className="size-4 animate-spin" />
        </div>
      )}

      <FinancialForm form={financialForm} />
      <OtherFinancialForm form={otherFinancialForm} />
      {renderEditButtons()}
    </div>
  );
};

export { FinancialTab };
